import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertCircle,
  Boxes,
  Box,
  Flame,
  Gauge,
  HelpCircle,
  LineChart,
  Package,
  RefreshCw,
  Settings2,
  Users,
  Zap,
} from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useVisiblePolling } from '../hooks/useVisiblePolling';
import { throttleApiService } from '../services/throttleApi';
import type {
  AntiLagData,
  CacheEnvelope,
  ThrottleServerDetail,
} from '../types/throttle';
import { FlaggedChunksTable } from '../components/throttle/FlaggedChunksTable';
import {
  MsptRuleTable,
  PlayerCountRuleTable,
  UnknownRulesNote,
} from '../components/throttle/ThrottleRuleTables';
import { PerformanceDumpsPanel } from '../components/throttle/PerformanceDumpsPanel';
import { CircularProgress } from '../components/status/CircularProgress';
import { PlayerLink } from '../components/PlayerLink';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeader } from '../components/ui/SectionHeader';
import { StatTile } from '../components/ui/StatTile';
import { EmptyState } from '../components/ui/EmptyState';
import {
  MSPT_TEXT_CLASSES,
  TICK_BUDGET_MS,
  describeThrottleMulti,
  describeThrottleTarget,
  formatEpochMsAgo,
  formatEpochMsDuration,
  getFlaggingFloorMspt,
  getMsptSeverity,
  getTpsGaugeColor,
  resolveEffectivePlayerCountThrottles,
  resolveEffectiveThrottleConfigs,
} from '../utils/throttle';
import { cn } from '../utils/cn';

/** Detail routes share the collection cadence: poll every 60–90 s with jitter. */
const POLL_MS = 60_000 + Math.round(Math.random() * 20_000);

/** Skip state updates when nothing new was cached (throttlingHash + cache timestamps). */
const isSameSnapshot = (a: ThrottleServerDetail, b: ThrottleServerDetail): boolean =>
  a.status?.throttlingHash === b.status?.throttlingHash &&
  a.status?.perfWrittenAt === b.status?.perfWrittenAt &&
  a.perf?.fetchedAt === b.perf?.fetchedAt &&
  a.throttling?.fetchedAt === b.throttling?.fetchedAt &&
  a.dumps.count === b.dumps.count &&
  a.dumps.lastDumpAt === b.dumps.lastDumpAt;

/** Compact icon + label / value metric row inside the Performance card. */
const MetricRow = ({
  icon: Icon,
  label,
  value,
  valueClassName,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
}) => (
  <div className="flex items-center justify-between gap-3 py-1.5 text-xs">
    <span className="flex items-center gap-1.5 text-glass-400">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </span>
    <span className={cn('text-white font-medium tabular-nums text-right', valueClassName)}>{value}</span>
  </div>
);

export const ChunkThrottleServerView = () => {
  const { fqdn } = useParams<{ fqdn: string }>();
  useDocumentTitle(fqdn ? `Chunk Throttle — ${fqdn}` : 'Chunk Throttle');

  const [detail, setDetail] = useState<ThrottleServerDetail | null>(null);
  const [config, setConfig] = useState<CacheEnvelope<AntiLagData> | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refresh = useCallback(async () => {
    if (!fqdn) return;
    setIsRefreshing(true);
    try {
      const result = await throttleApiService.getServer(fqdn);
      if (result === null) {
        setNotFound(true);
      } else {
        setNotFound(false);
        setDetail((prev) => (prev && isSameSnapshot(prev, result) ? prev : result));
      }
      setError(null);
    } catch (err) {
      // Keep the last good snapshot and surface a soft failure.
      setError(err instanceof Error ? err.message : 'Failed to load server data');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [fqdn]);

  useEffect(() => {
    setDetail(null);
    setNotFound(false);
    setError(null);
    setIsLoading(true);
    void refresh();
    throttleApiService
      .getConfig()
      .then(setConfig)
      .catch(() => setConfig(null));
  }, [refresh]);

  useVisiblePolling(() => void refresh(), POLL_MS);

  const status = detail?.status ?? null;
  const perf = detail?.perf ?? null;
  const throttling = detail?.throttling ?? null;

  const mspt = status?.mspt ?? perf?.data.mspt ?? null;
  const tps = status?.tps ?? perf?.data.tps ?? null;
  const clusterId = status?.clusterId ?? perf?.data.clusterId ?? null;
  const serverId = status?.serverId ?? perf?.data.serverId ?? null;
  /** Population tier the API reports as in force server-wide (`null` = none). */
  const activeServerWideThrottle = status?.serverWideThrottle ?? null;

  const configRules = useMemo(() => config?.data.chunkThrottleConfigs ?? [], [config]);

  /** Per-chunk MSPT tiers in force here. */
  const effectiveConfigs = useMemo(
    () => resolveEffectiveThrottleConfigs(configRules, fqdn, clusterId),
    [configRules, fqdn, clusterId],
  );

  /**
   * Server-wide population throttles configured for this server. Precedence is
   * resolved per rule family, so these still show when a chunk tier targets the
   * server directly. The API says which one is actually in force.
   */
  const playerCountConfigs = useMemo(
    () => resolveEffectivePlayerCountThrottles(configRules, fqdn, clusterId),
    [configRules, fqdn, clusterId],
  );

  /** Lowest tier threshold for regular chunks — reference for "×N threshold" annotations. */
  const flaggingThreshold = useMemo(() => getFlaggingFloorMspt(effectiveConfigs), [effectiveConfigs]);

  const monoTitle = <span className="font-mono break-all">{fqdn}</span>;

  // ── Loading ────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          backTo="/chunk-throttle"
          backLabel="Chunk Throttle"
          eyebrow="Infrastructure"
          title={monoTitle}
          description="Loading server data..."
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <StatTile key={i} label="" value="" loading />
          ))}
        </div>
        <div className="glass-card p-4 md:p-5" aria-busy="true">
          <SkeletonLoader className="h-4 w-1/3 mb-4" />
          <SkeletonLoader className="h-4 w-full mb-2" />
          <SkeletonLoader className="h-4 w-2/3" />
        </div>
      </div>
    );
  }

  // ── Unknown server (404) — also covers servers pruned after going offline ──
  if (notFound) {
    return (
      <div className="space-y-6">
        <PageHeader
          backTo="/chunk-throttle"
          backLabel="Chunk Throttle"
          eyebrow="Infrastructure"
          title={monoTitle}
        />
        <EmptyState
          icon={HelpCircle}
          title="Unknown Server"
          description={
            <>
              <span className="font-mono text-glass-300">{fqdn}</span> is not in the throttle cache.
              New servers can take a few minutes to be discovered; if this address looks wrong,
              head back to the overview and pick a server from the list.
            </>
          }
          action={
            <Link to="/chunk-throttle" className="glass-button">
              Back to overview
            </Link>
          }
        />
      </div>
    );
  }

  // ── Hard error with no snapshot ────────────────────────────────────────
  if (error && !detail) {
    return (
      <div className="space-y-6">
        <PageHeader
          backTo="/chunk-throttle"
          backLabel="Chunk Throttle"
          eyebrow="Infrastructure"
          title={monoTitle}
        />
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to Load Server Data"
          description={error}
          action={
            <button onClick={() => void refresh()} className="glass-button">
              Retry
            </button>
          }
        />
      </div>
    );
  }

  const asOfTooltip = [
    perf ? `Cached by poller: ${new Date(perf.fetchedAt).toLocaleString()}` : null,
    status?.perfWrittenAt
      ? `Perf record written: ${new Date(status.perfWrittenAt).toLocaleString()}`
      : null,
    perf?.data.timestamp
      ? `Measured on game server: ${new Date(perf.data.timestamp).toLocaleString()}`
      : null,
  ]
    .filter(Boolean)
    .join('\n');

  // Any perf field may be absent — only render players we can actually link.
  const players = (perf?.data.players ?? []).filter(
    (player): player is { UUID: string; name?: string } => typeof player.UUID === 'string',
  );

  const playerCount = status?.playerCount ?? perf?.data.playerCount;
  const maxPlayers = status?.maxPlayers ?? perf?.data.maxPlayers;
  const flaggedChunkCount = status?.flaggedChunkCount ?? null;

  const hasMetaLine =
    Boolean(clusterId && serverId) ||
    Boolean(perf?.data.modPackName) ||
    Boolean(status && !status.online && status.lastSeenAt != null);

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        backTo="/chunk-throttle"
        backLabel="Chunk Throttle"
        eyebrow="Infrastructure"
        title={monoTitle}
        titleAdornment={
          <>
            {status && (
              <span className={cn('badge badge-dot', status.online ? 'badge-success' : 'badge-danger')}>
                {status.online ? 'Online' : 'Offline'}
              </span>
            )}
            {activeServerWideThrottle && (
              <span
                className="badge badge-amber cursor-default"
                title={`Population throttle "${activeServerWideThrottle.id}" is in force: at ${activeServerWideThrottle.minPlayerCount}+ players the whole server runs at ${describeThrottleMulti(activeServerWideThrottle.throttleMulti)}.`}
              >
                <Gauge className="w-3 h-3" />
                Throttled ×{activeServerWideThrottle.throttleMulti}
              </span>
            )}
          </>
        }
        description={
          hasMetaLine ? (
            <span className="inline-flex flex-wrap items-center gap-2">
              {clusterId && serverId && (
                <span className="chip">
                  {clusterId} · {serverId}
                </span>
              )}
              {perf?.data.modPackName && <span>{perf.data.modPackName}</span>}
              {status && !status.online && status.lastSeenAt != null && (
                <span>Last seen {formatEpochMsAgo(status.lastSeenAt)}</span>
              )}
            </span>
          ) : undefined
        }
        meta={
          perf ? (
            <span className="cursor-default tabular-nums" title={asOfTooltip}>
              as of {formatEpochMsAgo(perf.fetchedAt)}
            </span>
          ) : undefined
        }
        actions={
          <>
            {/* Lag telemetry answers "what is slow now"; StarChart answers "since when". */}
            <Link
              to={`/starchart/${encodeURIComponent(fqdn ?? '')}`}
              className="glass-button touch-manipulation"
              title="Player count, TPS and popularity history for this server"
            >
              <LineChart className="w-4 h-4" />
              History
            </Link>
            <button
              onClick={() => void refresh()}
              className="glass-button touch-manipulation"
              disabled={isRefreshing}
              title="Refresh now"
            >
              <RefreshCw className={cn('w-4 h-4', isRefreshing && 'animate-spin')} />
              Refresh
            </button>
          </>
        }
      />

      {/* Refresh failed but we still have a snapshot. */}
      {error && detail && (
        <div className="alert alert-warning">
          <AlertCircle />
          <p>
            Refresh failed — showing the last cached snapshot.
          </p>
        </div>
      )}

      {/* Performance */}
      {status || perf ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile
              label="TPS"
              icon={Gauge}
              tone="accent"
              value={tps != null ? tps.toFixed(1) : <span className="text-glass-500">—</span>}
              hint="max 20"
            />
            <StatTile
              label="MSPT"
              icon={Zap}
              tone="purple"
              value={
                mspt != null ? (
                  <span className={MSPT_TEXT_CLASSES[getMsptSeverity(mspt)]}>{mspt.toFixed(1)} ms</span>
                ) : (
                  <span className="text-glass-500">—</span>
                )
              }
              hint={`tick budget ${TICK_BUDGET_MS} ms`}
            />
            <StatTile
              label="Players"
              icon={Users}
              tone="info"
              value={
                playerCount != null
                  ? `${playerCount}/${maxPlayers ?? '?'}`
                  : <span className="text-glass-500">—</span>
              }
            />
            <StatTile
              label="Flagged chunks"
              icon={Flame}
              tone={flaggedChunkCount != null && flaggedChunkCount > 0 ? 'amber' : 'neutral'}
              value={
                flaggedChunkCount != null ? (
                  <span className={cn(flaggedChunkCount > 0 && 'text-amber-300')}>{flaggedChunkCount}</span>
                ) : (
                  <span className="text-glass-500">—</span>
                )
              }
              hint={
                status?.flaggedSince != null && flaggedChunkCount != null && flaggedChunkCount > 0
                  ? `lagging for ${formatEpochMsDuration(status.flaggedSince)}`
                  : undefined
              }
            />
          </div>

          <div className="glass-card p-4 md:p-5">
            <SectionHeader as="h2" icon={Gauge} title="Performance" />
            <div className="flex flex-col sm:flex-row gap-6">
              <div className="flex items-center justify-center sm:justify-start gap-5 flex-shrink-0">
                <CircularProgress
                  value={tps ?? 0}
                  maxValue={20}
                  color={getTpsGaugeColor(tps ?? 0)}
                  size="w-20 h-20"
                  label={tps != null ? tps.toFixed(1) : '?'}
                  subLabel="TPS (max 20)"
                  showPercentage={false}
                />
                <div>
                  <p
                    className={cn(
                      'text-2xl font-semibold tracking-tight tabular-nums',
                      mspt != null ? MSPT_TEXT_CLASSES[getMsptSeverity(mspt)] : 'text-glass-500',
                    )}
                  >
                    {mspt != null ? `${mspt.toFixed(1)} ms` : '—'}
                  </p>
                  <p className="text-xs text-glass-400 mt-1">
                    MSPT — tick budget {TICK_BUDGET_MS} ms
                  </p>
                  <p className="text-[11px] text-glass-500 mt-0.5 max-w-[220px]">
                    Prefer MSPT over TPS for load: TPS is capped at 20, MSPT keeps ranking servers.
                  </p>
                </div>
              </div>

              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6 content-start sm:divide-x sm:divide-glass-200/10">
                <div className="divide-y divide-glass-200/10">
                  <MetricRow
                    icon={Users}
                    label="Players"
                    value={
                      playerCount != null
                        ? `${playerCount}/${maxPlayers ?? '?'}`
                        : '—'
                    }
                  />
                  <MetricRow
                    icon={Boxes}
                    label="Entities"
                    value={perf?.data.entities != null ? perf.data.entities.toLocaleString() : '—'}
                  />
                  <MetricRow
                    icon={Box}
                    label="Loaded chunks"
                    value={perf?.data.chunks != null ? perf.data.chunks.toLocaleString() : '—'}
                  />
                  <MetricRow
                    icon={Zap}
                    label="Redis ping"
                    value={perf?.data.redisPing != null ? `${perf.data.redisPing} ms` : '—'}
                  />
                </div>
                <div className="divide-y divide-glass-200/10 sm:pl-6">
                  <MetricRow
                    icon={Package}
                    label="Minecraft"
                    value={perf?.data.mcVersion ?? '—'}
                  />
                  <MetricRow
                    icon={Package}
                    label="Modloader"
                    value={
                      perf?.data.modloader
                        ? `${perf.data.modloader} ${perf.data.modloaderVersion ?? ''}`.trim()
                        : '—'
                    }
                  />
                  <MetricRow
                    icon={Package}
                    label="Quasar"
                    value={perf?.data.quasarVersion ?? '—'}
                  />
                  <MetricRow
                    icon={Box}
                    label="Host"
                    valueClassName="font-mono text-xs break-all"
                    value={
                      perf?.data.hostname
                        ? `${perf.data.hostname}${perf.data.serverPort != null ? `:${perf.data.serverPort}` : ''}`
                        : '—'
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="glass-card p-4 md:p-5">
          <SectionHeader as="h2" icon={Gauge} title="Performance" className="mb-2" />
          <p className="text-sm text-glass-400">
            No performance data has been cached for this server yet.
          </p>
        </div>
      )}

      {/* Flagged chunks (live feed) */}
      <div className="glass-card p-4 md:p-5">
        <SectionHeader
          as="h2"
          icon={Flame}
          iconClassName="text-amber-300"
          title="Flagged Chunks"
          description="Live feed of the ≤10 worst currently problematic chunks per dimension, refreshed every minute. Flagged = WARNING or THROTTLED — the two states are not distinguishable here."
          actions={
            status?.flaggedSince != null && status.flaggedChunkCount > 0 ? (
              <span
                className="badge badge-amber cursor-default"
                title={`Episode started ${new Date(status.flaggedSince).toLocaleString()}`}
              >
                lagging for {formatEpochMsDuration(status.flaggedSince)}
              </span>
            ) : undefined
          }
        />
        {throttling ? (
          <FlaggedChunksTable
            chunks={throttling.data.problematicChunks}
            flaggingThreshold={flaggingThreshold}
            mspt={mspt}
          />
        ) : (
          <p className="text-sm text-glass-400">
            No throttling data has been cached for this server yet.
          </p>
        )}
      </div>

      {/* Staff-triggered performance dumps */}
      {detail && (
        <PerformanceDumpsPanel
          fqdn={detail.fqdn}
          dumps={detail.dumps}
          flaggingThreshold={flaggingThreshold}
        />
      )}

      {/* Effective anti-lag config */}
      <div className="glass-card p-4 md:p-5">
        <SectionHeader as="h2" icon={Settings2} title="Effective Anti-Lag Config" />
        {effectiveConfigs.length > 0 || playerCountConfigs.length > 0 ? (
          <div className="space-y-4">
            {effectiveConfigs.length > 0 && (
              <div className="subcard p-3 md:p-4">
                <p className="text-xs text-glass-500 mb-3" title={effectiveConfigs[0].target}>
                  {effectiveConfigs.length} chunk tier{effectiveConfigs.length === 1 ? '' : 's'} via{' '}
                  {describeThrottleTarget(effectiveConfigs[0].target)} — heavier chunks fall under
                  harsher tiers.
                </p>
                <MsptRuleTable rules={effectiveConfigs} />
              </div>
            )}

            {playerCountConfigs.length > 0 && (
              <div className="subcard p-3 md:p-4">
                <h3 className="text-sm font-semibold text-white mb-1">Server-wide population throttle</h3>
                <p className="text-xs text-glass-500 mb-3" title={playerCountConfigs[0].target}>
                  {activeServerWideThrottle ? (
                    <>
                      The whole server is running at{' '}
                      {describeThrottleMulti(activeServerWideThrottle.throttleMulti)} under rule{' '}
                      <span className="font-mono text-glass-400">{activeServerWideThrottle.id}</span>
                      {status?.playerCount != null && ` (${status.playerCount} players online)`}.
                    </>
                  ) : (
                    <>
                      No population tier is in force — the server is below every tier
                      {status?.playerCount != null && ` (${status.playerCount} players online)`}.
                    </>
                  )}
                </p>
                <PlayerCountRuleTable
                  rules={playerCountConfigs}
                  activeThrottle={activeServerWideThrottle}
                />
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-glass-400">
            No configured rules target this server — it runs on the plugin's built-in defaults. The
            full rule list is on the{' '}
            <Link to="/chunk-throttle" className="text-accent-300 hover:text-accent-200 hover:underline underline-offset-2">
              overview page
            </Link>
            .
          </p>
        )}
        <UnknownRulesNote rules={configRules} />
      </div>

      {/* Online players */}
      {players.length > 0 && (
        <div className="glass-card p-4 md:p-5">
          <SectionHeader as="h2" icon={Users} title="Online Players" count={players.length} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {players.map((player) => (
              <div key={player.UUID} className="p-2 subcard">
                <PlayerLink uuid={player.UUID} name={player.name} showUuid={false} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
